import type { BasicTableV3ColumnDataType } from "@/components/table/v3/BasicTableV3/BasicTableV3.columnDataTypes";
import type {
  BasicTableV3ColumnFormat,
  BasicTableV3ColumnFormatOption,
} from "@/components/table/v3/BasicTableV3/BasicTableV3.formats";
import type { NonIdealStateProps } from "@/components/interactive/NonIdealState";
import type { SkeletonShape } from "@/components/loaders/Skeleton";
import { QueryParam } from "@/utils/hooks/url/use-query-state";
import {
  type ColumnFiltersState,
  type ColumnDef,
  type ColumnOrderState,
  type ColumnPinningState,
  type ColumnSizingState,
  type ExpandedState,
  type OnChangeFn,
  type RowData,
  type RowSelectionState,
  type SortingState,
  type Table,
  type TableOptions,
  type VisibilityState,
} from "@tanstack/react-table";
import type React from "react";
import type { Dispatch, ReactNode, SetStateAction } from "react";

export type BasicTableV3Mode = "table" | "grid";
export type BasicTableV3ColumnAlignment = "center" | "left" | "right";
export type BasicTableV3ColumnType = "string" | "numeric";
export type BasicTableV3CsvSerializer = (value: unknown) => string;

export interface BasicTableV3CsvOptions {
  headerName?: string;
  serializer?: BasicTableV3CsvSerializer;
}
export type BasicTableV3DropPlacement = "before" | "after";
export type BasicTableV3DragSurface = "header" | "menu";
export type BasicTableV3ColumnSection = "frozen" | "scrolling";
export type BasicTableV3RowHeight = "compact" | "default" | "lg" | "xl" | "tall";
/** A click, or an Enter/Space keypress on the focused row. */
export type BasicTableV3RowActivationEvent =
  | React.MouseEvent<HTMLTableRowElement>
  | React.KeyboardEvent<HTMLTableRowElement>;
export type BasicTableV3VirtualizationMode = "full-page" | "clamped";
export type BasicTableV3NoVirtualization = "none";
// "string" filters take free text typed into the value segment's popover and
// match by substring ("contains" / "does not contain") or by leading fragment
// ("starts with").
export type BasicTableV3FilterDataType = "enum" | "numeric" | "string";
// "includes"/"excludes" were renamed to "is any of"/"is not"; legacy query
// state is translated by normalizeLegacyBasicTableV3FilterOperator.
export type BasicTableV3FilterOperator =
  | "<"
  | "≤"
  | "="
  | "≥"
  | ">"
  | "is any of"
  | "is not"
  | "contains"
  | "does not contain"
  // Server-only: no client-side matcher applies it, so a column offering it must
  // encode it into the request rather than filter loaded rows.
  | "starts with";

export interface BasicTableV3DragTarget {
  columnId: string | null;
  placement: BasicTableV3DropPlacement;
  surface: BasicTableV3DragSurface;
  section?: BasicTableV3ColumnSection;
}

export interface BasicTableV3ColumnConfig<TData extends RowData = RowData> {
  /**
   * Canonical alignment for both the header and body cells. When omitted,
   * numeric columns align right and string columns align left.
   */
  align?: BasicTableV3ColumnAlignment;
  /** Resolve a truncated image preview only when its tall cell approaches the viewport. */
  loadImageValue?: (row: TData) => Promise<unknown>;
  /** Extra classes for loaded body-cell shells, such as removing padding for a full-cell control. */
  bodyCellClassName?: string;
  /**
   * The cell hosts controls (buttons, menus) rather than text. Its shell does not clip and its
   * content is not truncated, so a focus ring or hover-revealed chrome shows in full instead of
   * being cut at the cell's edges. `stickyRight` columns render this way automatically.
   */
  controls?: boolean;
  /**
   * Let this column's cells wrap onto as many lines as their content needs, instead of clamping to
   * one ellipsized line. The cell keeps its column width and still clips horizontally; only the
   * vertical clamp is lifted, and the row grows to fit because `rowHeight` is a floor on a table
   * row rather than a fixed height.
   *
   * For a column whose value is prose a reader has to read in full — a diagnostic message, an error
   * body — where a tooltip is the wrong home for the primary content. Everything else should stay
   * truncated: a table of one-line cells is scannable, and one wrapping column costs every row a
   * variable height. Pair it with `rowVirtualization: BASIC_TABLE_V3_NO_VIRTUALIZATION`, since the
   * virtualizers size rows from a single `estimatedRowHeight`.
   */
  wrapContent?: boolean;
  csvOptions?: BasicTableV3CsvOptions;
  /**
   * Hold this column at the end of the column order. Columns the table has not
   * seen before are appended to the stored order, so a fixed trailing column
   * (an "add column" slot, a trailing actions column) would otherwise be
   * overtaken by every column added after it. Ordering only — unlike `pinned`
   * and `stickyRight`, this column still scrolls with the rest. Applied when
   * the column list is reconciled, not on drag-reorder, so pair it with
   * `disableDragging` if the table lets columns be dragged past it.
   */
  alwaysLast?: boolean;
  /**
   * The semantic type of this column's values ("datetime", "boolean", "list",
   * ...), surfaced as an icon before the header label when the table sets
   * `showColumnTypeIcons`. Presentation only — it does not affect sorting,
   * filtering, formatting, or alignment. Omit it and the table falls back to
   * `type` (numeric → number, string → text); the columns-v3 helpers set it for
   * you.
   */
  dataType?: BasicTableV3ColumnDataType;
  disableDragging?: boolean;
  disableResizing?: boolean;
  /**
   * Keep this column out of `showColumnTypeIcons`. For synthetic chrome columns
   * whose header does not describe data — row numbers, selection checkboxes,
   * trailing action columns — where a type icon is noise.
   */
  hideTypeIcon?: boolean;
  /**
   * This column's `cell` renderer applies the customize-mode display format
   * itself, so the cell shell must not substitute the formatted value for it.
   * For cells that need the raw value alongside the formatted one — the notebook
   * input grid formats for display but edits the underlying text.
   */
  ownsFormatting?: boolean;
  /**
   * Curated display formats exposed from a trailing formatting action in the
   * column header. Omit this when the column does not support user-selectable
   * formatting. Custom cell renderers should also set `ownsFormatting` and
   * read the active format from `useBasicTableV3ColumnFormats`.
   */
  formatOptions?: readonly BasicTableV3ColumnFormatOption[];
  filterConfig?: BasicTableV3ColumnFilterConfig<TData>;
  /**
   * This column's backend sort identity. Consumed by the server-table
   * controller and ignored by client-side tables.
   */
  serverSort?: BasicTableV3ServerColumnSort;
  /**
   * Full-width block rendered BELOW the header's label row — a distribution
   * strip, a sparkline. It sits outside the sort button and outside the row that
   * holds the header chrome, so the band spans the whole column while the type
   * icon, sort, filter, format and drag affordances stay beside the label
   * instead of centering against a much taller cell.
   *
   * The height is declared, not measured: the table renders the band in exactly
   * this many pixels and derives the header's height from it, so the chrome that
   * lines up with the header's bottom edge (scroll cliff, frozen-edge overlay,
   * clamped scrollport budget) is right on the first paint.
   *
   * As soon as one visible column defines a band, every column's label row is
   * top-aligned — otherwise a band-less column's label (and the row-number
   * column's "#") would float at the mid-height of the taller header and read as
   * a different row.
   */
  headerBand?: { content: ReactNode; height: number };
  /**
   * Secondary text rendered after the column title. It truncates independently
   * from the title so metadata such as an exact source datatype cannot change
   * which part of a long column name remains visible.
   */
  headerTrailingLabel?: string;
  /** Additional explanation shown when hovering the column header label. */
  headerTooltip?: ReactNode;
  /** Persistent action rendered directly after the column title, outside its sort button. */
  headerTrailingAction?: ReactNode;
  id: string;
  initialFrozen?: boolean;
  initialHidden?: boolean;
  /** Added by the user rather than part of the default set, so its presence is itself a preference. */
  optional?: boolean;
  label: string;
  /** Group related columns under a source heading in the Columns menu. */
  visibilityGroup?: string;
  queryParam?: QueryParam;
  queryParamShorthand?: string;
  /**
   * Extra classes (usually a width) for this column's skeleton cell when the table renders with
   * `loading`. Pass a function of the skeleton row index for deterministic per-row variation
   * (e.g. log-message lines of differing lengths).
   */
  skeletonCellClassName?: string | ((rowIndex: number) => string);
  skeletonCellShape?: SkeletonShape;
  /**
   * Fix this column to the right edge, including during horizontal column virtualization.
   * Multiple right pins use cumulative offsets and may coexist with left-frozen columns.
   * Users cannot drag it into or out of the frozen section. Control cells render
   * overflow-visible so hover toolbars and focus rings can extend past the cell.
   * Build row-actions columns with getRowOptionsColumnV3, which owns the shared icon-only
   * "more" trigger and renders the row's sections and items from props.
   */
  stickyRight?: boolean;
  /**
   * Pin this column to the table's `pinSide` edge via column pinning. Like `stickyRight`, these
   * columns stay mounted under horizontal virtualization. Only meaningful when the
   * table sets `pinSide`; `stickyRight` independently adds fixed right pins.
   */
  pinned?: boolean;
  type: BasicTableV3ColumnType;
  size: number;
}

export interface BasicTableV3ColumnFilterConfig<TData extends RowData = RowData> {
  /** Let users enter exact values outside the suggestion list. Suggestions
   *  remain useful for discovery without claiming to be the full domain. */
  allowCustomValues?: boolean;
  defaultOperator: BasicTableV3FilterOperator;
  defaultValue: string | string[];
  /**
   * Whether this filter is available in the current table instance. Keep the
   * config and its server encoder present when a page-level scope temporarily
   * locks the column; this preserves the controller's typed schema without
   * exposing the filter or adopting/writing its URL parameter.
   */
  enabled?: boolean;
  isMultiValue?: boolean;
  /** Offer every resolved value option in the pill's value menu, instead of only
   *  those present in loaded rows. For server-side filters whose options come
   *  from a wider source (e.g. an RPC list) than the current page of data. */
  /** @deprecated Value menus now always list every resolved option (with
   *  faceted counts); this flag no longer changes behavior. */
  showAllValueOptions?: boolean;
  operatorOptions: readonly BasicTableV3FilterOperator[];
  /**
   * Server-driven meaning of this filter. Keeping the encoder with the filter
   * config makes it impossible to describe a server filter without also
   * defining the chip's operators, values, and labels.
   */
  server?: BasicTableV3ServerColumnFilter;
  type: BasicTableV3FilterDataType;
  valueOptions?: readonly string[];
  getValueOptions?: (rows: readonly TData[]) => readonly string[];
  /** Format an option for display without changing its stored filter value. */
  getValueLabel?: (value: string) => string;
  /** Full value text shown on hover without making the menu label verbose. */
  getValueTooltip?: (value: string) => string | undefined;
  /** Group enumerable values under titled sections in filter value menus. */
  getValueGroup?: (value: string) => string | undefined;
  /** Keep selected custom values out of the suggestion list when the menu
   *  should contain only its canonical presets. Defaults to true. */
  showSelectedCustomValuesInMenu?: boolean;
  /** Override the value-menu input placeholder. Defaults to a generated
   *  "Search [column values]" label. */
  valueSearchPlaceholder?: string;
  /** Muted second line under a value option in value menus (e.g. an agent's
   *  email). Menus only — chips render the flat label. */
  getValueDescription?: (value: string) => string | undefined;
  /** Value-menu width in px (default 220). Widen for columns whose labels or
   *  metadata run long; text that still clips reveals a tooltip. */
  valueMenuWidthPx?: number;
  /** Server-searched value options: value menus always show their search input and report the
   *  typed text here, so the owner can refetch `valueOptions` for it. An empty option list is
   *  then a valid search result, and the column stays filterable. */
  onValueSearchChange?: (query: string) => void;
  /** Value menus call this when scrolled near their end, so the owner can load more
   *  `valueOptions`. */
  onValueMenuScrollEnd?: () => void;
  /** Shows a loading row at the end of value menus while more options load. */
  valueMenuLoadingMore?: boolean;
  /** Caps the height of value menus' option list in px. Unset, the list grows to the
   *  viewport's bottom edge. */
  valueMenuMaxHeightPx?: number;
  /** Shows a thin scrollbar on value menus' option list while it overflows. Hidden
   *  by default. */
  valueMenuShowScrollbar?: boolean;
}

export interface BasicTableV3ActiveFilter<TColumnId extends string = string> {
  columnId: TColumnId;
  id: string;
  matchesNothing?: boolean;
  operator: BasicTableV3FilterOperator;
  value: string | string[];
}

export interface BasicTableV3ColumnScopedFilter<TColumnId extends string = string> {
  columnId: TColumnId;
}

/** A server filter can be active yet have no representable request value
 * because it excludes its complete closed domain. Keep that distinct from
 * `undefined`, which means the request field is intentionally omitted. */
export const BASIC_TABLE_V3_MATCHES_NO_ROWS: unique symbol = Symbol("BasicTableV3MatchesNoRows");

export type BasicTableV3ServerFilterResult<TFilterValue> =
  | TFilterValue
  | typeof BASIC_TABLE_V3_MATCHES_NO_ROWS
  | undefined;

/** How a column's active chip becomes a request value. `encode` returns the
 *  value for the request field — any shape (array, scalar, boolean);
 *  `BASIC_TABLE_V3_MATCHES_NO_ROWS` means the active filter cannot match any
 *  row; `undefined` means "omit this field" (no filter). `facetKey` is an
 *  optional hook for matching server-provided facet counts later. */
export interface BasicTableV3ServerColumnFilter<TFilterValue = unknown> {
  encode: (
    filter: Pick<BasicTableV3ActiveFilter, "operator" | "value">,
  ) => BasicTableV3ServerFilterResult<TFilterValue>;
  facetKey?: string;
}

/** A column's backend sort identity (e.g. a proto sort-column enum value). The
 *  controller pairs it with a direction to build the request's sort fields. */
export interface BasicTableV3ServerColumnSort<TSortValue = unknown> {
  value: TSortValue;
}

export type BasicTableV3Column<TData extends RowData = RowData, TValue = any> = ColumnDef<
  TData,
  TValue
> &
  BasicTableV3ColumnConfig<TData>;

export type BasicTableV3ResolvedFilterColumn<
  TData extends RowData = RowData,
  TColumnId extends string = string,
> = BasicTableV3Column<TData> & {
  id: TColumnId;
  filterConfig: BasicTableV3ColumnFilterConfig<TData>;
  resolvedValueOptions: string[];
};

export type BasicTableV3DisplayColumn<TData extends RowData = RowData> =
  BasicTableV3Column<TData> & {
    frozen: boolean;
  };

export type BasicTableV3QueryStateMode = "read-write" | "read-only" | "none";

export interface BasicTableV3QueryStateOptions {
  queryParamNamespace?: string;
  /**
   * Controls how column visibility, frozen columns, and filters interact with
   * query state. "read-write" (default) round-trips query params and uses
   * page-scoped local storage as the initial filter fallback when the URL is
   * silent. "read-only" adopts query params without writing them or local
   * storage. "none" ignores query state entirely, which prevents embedded or
   * sibling tables from sharing the page-global parameter keys. Use
   * queryParamNamespace to isolate multiple read-write tables on one page.
   */
  queryStateMode?: BasicTableV3QueryStateMode;
}

export interface BasicTableV3VirtualizationOptions {
  /**
   * Height of the header's LABEL ROW, for hosts whose header is not the standard
   * one. A column's `headerBand` height is added to this, so a host never
   * has to account for bands here.
   */
  estimatedHeaderHeight?: number;
  estimatedRowHeight?: number;
  /**
   * Clamped mode only: size the scroll container to the full `maxVisibleRows`
   * budget even when there are fewer rows, leaving empty canvas below the last
   * row. For hosts that hand the table a fixed frame it must fill (e.g.
   * dashboard tiles); without it a short table shrinks to its row count.
   */
  fillClampedHeight?: boolean;
  /** Clamped fill mode: the body's budget in pixels, for a frame that is not a whole number of
   *  rows. Without it the body rounds down, leaving up to a row of the host's frame unused. */
  clampedBodyHeightPx?: number;
  maxVisibleRows?: number;
  mode: BasicTableV3VirtualizationMode;
  overscan?: number;
}

export type BasicTableV3Virtualization =
  | BasicTableV3NoVirtualization
  | BasicTableV3VirtualizationOptions;

export interface UseBasicTableV3QueryStateResult {
  readStoredColumnFilters: () => ColumnFiltersState | null;
  queryColumnFilters: ColumnFiltersState;
  queryFrozenColumns: string[] | null;
  queryVisibleColumns: string[] | null;
  setQueryColumnFilters: (
    updater: ColumnFiltersState | ((current: ColumnFiltersState) => ColumnFiltersState),
    options?: { history?: "push" | "replace" },
  ) => ColumnFiltersState;
  setQueryFrozenColumns: (frozenColumnIds: string[]) => Promise<URLSearchParams>;
  setQueryVisibleColumns: (visibleColumnIds: string[]) => Promise<URLSearchParams>;
}

export interface BasicTableV3TableState {
  columnOrder: ColumnOrderState;
  columnPinning: ColumnPinningState;
  columnSizing: ColumnSizingState;
  columnVisibility: VisibilityState;
  sorting: SortingState;
}

export interface BasicTableV3State<TFilter> extends BasicTableV3TableState {
  activeFilters: TFilter[];
  /** Customize-mode display formats by column id; absent columns render
   *  "automatic" (the column's own cell renderer). */
  columnFormats: Record<string, BasicTableV3ColumnFormat>;
  mode: BasicTableV3Mode;
}

export interface BasicTableV3InitialColumnLayout {
  columnOrder?: readonly string[];
  columnSizing?: Readonly<ColumnSizingState>;
  columnVisibility?: Readonly<VisibilityState>;
}

export interface UseBasicTableV3StateOptions<
  TData extends RowData,
  TFilter extends BasicTableV3ColumnScopedFilter,
> extends BasicTableV3QueryStateOptions {
  columns: readonly BasicTableV3Column<TData>[];
  initialActiveFilters?: readonly TFilter[];
  /** Restored display preferences. These may arrive after mount when the
   *  storage-backed value becomes available during browser hydration. */
  initialColumnFormats?: Readonly<Record<string, BasicTableV3ColumnFormat>>;
  /** Restored widths, visibility and order over the columns' own defaults; sparse, so an absent
   *  column keeps its default. */
  initialColumnLayout?: BasicTableV3InitialColumnLayout;
  initialMode?: BasicTableV3Mode;
  initialSorting?: SortingState;
  pinSide?: "left" | "right";
  /** Observes every effective active-filter change at the state setter itself
   *  (all mutation paths funnel through it), so server-driven tables can lift
   *  filters into request params in the SAME update — a post-render effect
   *  bridge costs an extra render cycle per change. Also invoked once on
   *  mount with the initial filters. */
  onActiveFiltersChange?: (activeFilters: readonly TFilter[]) => void;
  /** Observes format changes so a table can persist its display preferences
   *  without coupling the shared component to a storage backend. */
  onColumnFormatsChange?: (
    columnFormats: Readonly<Record<string, BasicTableV3ColumnFormat>>,
  ) => void;
  queryStateToFilters?: (
    queryColumnFilters: ColumnFiltersState,
    currentActiveFilters: readonly TFilter[],
  ) => TFilter[];
  /** Write the mount-time active filters into
   *  the filter query params when the URL holds none. Without this, filters
   *  that did not originate from the URL (e.g. migrated legacy params) vanish
   *  on reload because query params are only written on later mutations. */
  seedQueryStateFromInitialFilters?: boolean;
  filtersToQueryState?: (activeFilters: readonly TFilter[]) => ColumnFiltersState;
}

export interface UseBasicTableV3StateResult<
  TData extends RowData,
  TFilter extends BasicTableV3ColumnScopedFilter,
> {
  activeFilterColumnIds: ReadonlySet<string>;
  displayedColumns: BasicTableV3DisplayColumn<TData>[];
  frozenColumns: BasicTableV3DisplayColumn<TData>[];
  orderedColumns: BasicTableV3DisplayColumn<TData>[];
  scrollingColumns: BasicTableV3DisplayColumn<TData>[];
  /** The initial persisted filter state has been reconciled. Server queries
   *  should stay disabled until this is true. */
  isReady: boolean;
  state: BasicTableV3State<TFilter>;
  tableState: BasicTableV3TableState;
  /** Accepts an updater for decisions that must see FRESH filters: cache
   *  change notifications are batched, so captured state can trail a write
   *  from the same event. */
  setActiveFilters: (
    activeFilters: TFilter[] | ((currentActiveFilters: TFilter[]) => TFilter[]),
  ) => void;
  setColumnFormat: (columnId: string, format: BasicTableV3ColumnFormat) => void;
  setColumnWidth: (columnId: string, width: number) => void;
  setMode: (mode: BasicTableV3Mode) => void;
  setSorting: OnChangeFn<SortingState>;
  setState: Dispatch<SetStateAction<BasicTableV3State<TFilter>>>;
  /** Shows or hides every listed column at once. A hide that would leave no
   *  visible column keeps the first listed column that was showing. */
  setColumnsVisibility: (columnIds: readonly string[], visible: boolean) => void;
  toggleColumnVisibility: (columnId: string) => void;
  visibleColumnIds: string[];
  moveColumn: (
    draggedColumnId: string,
    targetColumnId: string | null,
    placement: BasicTableV3DropPlacement,
    targetSection: BasicTableV3ColumnSection,
  ) => void;
}

export interface TableV3ContextValue<
  TData extends RowData = RowData,
  TFilter extends BasicTableV3ColumnScopedFilter = BasicTableV3ColumnScopedFilter,
  TColumnId extends string = string,
> {
  activeFilterColumnIds: ReadonlySet<string>;
  availableFilterColumns: BasicTableV3ResolvedFilterColumn<TData, TColumnId>[];
  displayedColumns: BasicTableV3DisplayColumn<TData>[];
  createFilter?: (
    column: BasicTableV3ResolvedFilterColumn<TData, TColumnId>,
    overrides?: Partial<Pick<BasicTableV3ActiveFilter<TColumnId>, "operator" | "value">>,
  ) => TFilter;
  dragTarget: BasicTableV3DragTarget | null;
  draggedColumnId: string | null;
  filterColumnById: Partial<Record<TColumnId, BasicTableV3ResolvedFilterColumn<TData, TColumnId>>>;
  filterColumns: BasicTableV3ResolvedFilterColumn<TData, TColumnId>[];
  /** Frequently used filters offered as one-click ghost chips. */
  frequentFilterColumnIds: readonly TColumnId[];
  frozenColumns: BasicTableV3DisplayColumn<TData>[];
  getRowKey: (row: TData) => string;
  /** True only when loaded rows are the complete filter universe and their
   *  faceted counts can safely drive count badges and disabled options. */
  hasLoadedDataFaceting: boolean;
  moveColumn: (
    draggedColumnId: string,
    targetColumnId: string | null,
    placement: BasicTableV3DropPlacement,
    targetSection: BasicTableV3ColumnSection,
  ) => void;
  getRowHref?: (row: TData) => string | undefined;
  getRowMemoizationKey?: (row: TData) => unknown;
  onRowClick?: (row: TData, event: BasicTableV3RowActivationEvent) => void;
  onRowContextMenu?: (row: TData, event: React.MouseEvent<HTMLTableRowElement>) => void;
  getRowClassName?: (row: TData) => string | undefined;
  orderedColumns: BasicTableV3DisplayColumn<TData>[];
  onStartColumnDrag: (columnId: string, event: React.PointerEvent<HTMLElement>) => void;
  scrollingColumns: BasicTableV3DisplayColumn<TData>[];
  /** Present when a server-table controller owns filtering and sorting. */
  server?: {
    sorting: { columnId: string; direction: "asc" | "desc" } | null;
    toggleSorting: (columnId: string) => void;
  };
  /** Accepts an updater for decisions that must see FRESH filters: cache
   *  change notifications are batched, so captured state can trail a write
   *  from the same event. */
  setActiveFilters: (
    activeFilters: TFilter[] | ((currentActiveFilters: TFilter[]) => TFilter[]),
  ) => void;
  setColumnFormat: (columnId: string, format: BasicTableV3ColumnFormat) => void;
  setColumnWidth: (columnId: string, width: number) => void;
  setMode: (mode: BasicTableV3Mode) => void;
  state: BasicTableV3State<TFilter>;
  table: Table<TData>;
  /** Shows or hides every listed column at once. A hide that would leave no
   *  visible column keeps the first listed column that was showing. */
  setColumnsVisibility: (columnIds: readonly string[], visible: boolean) => void;
  toggleColumnVisibility: (columnId: string) => void;
  visibleColumnIds: string[];
}

export interface BasicTableV3ProviderProps<
  TData extends RowData,
  TFilter extends BasicTableV3ColumnScopedFilter,
  TColumnId extends string = string,
> extends UseBasicTableV3StateOptions<TData, TFilter> {
  children: ReactNode;
  columns: BasicTableV3Column<TData>[];
  createFilter?: (
    column: BasicTableV3ResolvedFilterColumn<TData, TColumnId>,
    overrides?: Partial<Pick<BasicTableV3ActiveFilter<TColumnId>, "operator" | "value">>,
  ) => TFilter;
  data: TData[];
  /** Whether loaded rows are the complete dataset for value faceting. Defaults
   *  to true when filters run client-side. Infinite/server-paginated tables
   *  with a compatibility client filter must set this false. */
  enableLoadedDataFaceting?: boolean;
  enableRowSelection?: TableOptions<TData>["enableRowSelection"];
  expanded?: ExpandedState;
  filterFromLeafRows?: boolean;
  filtersToColumnFilters?: (activeFilters: readonly TFilter[]) => ColumnFiltersState;
  /** Frequently used filters offered as one-click ghost chips. */
  frequentFilterColumnIds?: readonly TColumnId[];
  getExpandedRowModel?: TableOptions<TData>["getExpandedRowModel"];
  getRowHref?: (row: TData) => string | undefined;
  getRowKey: (row: TData) => string;
  /** Includes row fields and external state used by custom cells but not returned by their accessor.
   *  Accessor values are compared by identity and must be treated as immutable. */
  getRowMemoizationKey?: (row: TData) => unknown;
  getRowClassName?: (row: TData) => string | undefined;
  getSubRows?: TableOptions<TData>["getSubRows"];
  initialSorting?: SortingState;
  /**
   * Query status for the whole table. Owned here rather than on BasicTableV3
   * so the toolbar and the table read one source: the toolbar shows a spinner
   * while `updating`, and the table renders skeleton rows while `loading`.
   */
  loading?: boolean | { rows?: number };
  onExpandedChange?: OnChangeFn<ExpandedState>;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  onRowClick?: (row: TData, event: BasicTableV3RowActivationEvent) => void;
  onRowContextMenu?: (row: TData, event: React.MouseEvent<HTMLTableRowElement>) => void;
  rowSelection?: RowSelectionState;
  /** See `loading`. Rows stay mounted; only the busy affordance changes. */
  updating?: boolean;
  resolveFilterColumns?: (
    columns: readonly BasicTableV3Column<TData>[],
    data: readonly TData[],
  ) => readonly BasicTableV3ResolvedFilterColumn<TData, TColumnId>[];
}

export interface BasicTableV3Props {
  /**
   * Let columns sum to less than the container width, leaving empty space
   * after the last column instead of stretching it to fill. Intended for
   * non-virtualized tables: full-page virtualization draws its rounded border
   * around the container, which would enclose the empty space.
   */
  allowTrailingEmptySpace?: boolean;
  ariaLabel: string;
  /**
   * Which edge `pinned` columns stick to. "left" (default) also enables the drag-to-freeze flow;
   * "right" pins `pinned` columns to the right edge and disables user-controlled left freezing.
   */
  pinSide?: "left" | "right";
  /**
   * How `stickyRight` columns render while columns scroll beneath them. "frozen" paints an opaque
   * column with a boundary separator and shadow. "overlay" (default) drops the column chrome and
   * shows each row's controls only while the row is hovered or focused, floating over a
   * translucent backdrop that fades out the content behind it.
   * Either way the column keeps its own slot at the end of the table, so at the right scroll edge
   * the controls sit beside the last column rather than over it.
   */
  stickyRightPresentation?: "frozen" | "overlay";
  /**
   * Clamped mode only: grow the table to fill the parent's remaining height
   * (capped at the content height, so short tables stay short). The parent
   * must provide a definite height, e.g. a flex column with min-h-0 children.
   */
  fillAvailableHeight?: boolean;
  fillAvailableWidth?: boolean;
  /**
   * Grow the table area to at least the remaining scrollport height below it,
   * minus a bottom inset (defaults to the layout's horizontal padding so
   * the bottom gap matches the side gutters). Empty and sparse tables span the
   * viewport; taller tables scroll past it as usual. Not supported in clamped
   * virtualization mode, which sizes the container from row counts.
   */
  fillViewportHeight?: boolean | { bottomInsetPx?: number };
  className?: string;
  /** Customize mode: right-clicking a column header opens a context menu for editing that column's
   *  presentation. Defaults to false; enable it only where the result is worth keeping. */
  customizable?: boolean;
  /**
   * Customize mode only: extra menu content appended to a column's customize
   * menu, keyed by the right-clicked column's id. For tables whose columns are
   * themselves editable (the notebook input grid's rename / insert / delete),
   * so those actions live in the SAME menu as format and visibility instead of
   * a parallel one. Return null to leave a column's menu untouched.
   */
  renderColumnContextMenuSections?: (columnId: string) => ReactNode;
  /**
   * Spreadsheet-style cell selection: clicking a header or body cell selects
   * it (arrow keys move the selection), and ⌘C/Ctrl-C copies its rendered
   * text. Defaults to false.
   */
  enableCellSelection?: boolean;
  /** Defaults to false; set true to hide header sort controls for the whole table. */
  disableSorting?: boolean;
  /**
   * Where overflowing string header labels place their ellipsis. Defaults to
   * `end`; result grids can use `middle` to preserve recognizable prefixes and
   * suffixes in generated column names.
   */
  headerTextTruncation?: "end" | "middle";
  /**
   * Render each column's data-type icon before its header label (a result-grid
   * affordance: the header says what kind of values the column holds). The icon
   * comes from the column's `dataType`, falling back to its alignment `type`.
   * Defaults to false.
   */
  showColumnTypeIcons?: boolean;
  /**
   * Render flush to the container: no rounded corners and no outer border, so
   * gridlines and row highlighting run to the container's own edges. For hosts
   * that draw the table's frame themselves (the notebook result drawers).
   * Ignored under full-page virtualization, which owns its rounded frame.
   */
  frameless?: boolean;
  /**
   * Drop the column header row entirely. For a small table whose columns are self-evident from
   * their contents — a status badge beside a label — and which is titled by its own surrounding
   * section, so a header row would repeat what the section already said and spend a row doing it.
   *
   * The header is also where sorting, filtering, resizing and column drag live, so a headerless
   * table has none of those affordances; pair it with `disableSorting`. Intended for the
   * clamped/unvirtualized sizes, since under full-page virtualization the sticky container that
   * holds the header still renders for the super-header bar.
   */
  hideColumnHeaders?: boolean;
  /** @deprecated Prefer `disableSorting`; pass `false` to disable sorting. */
  enableSortingControls?: boolean;
  /**
   * Shown over the empty canvas, rendered through `NonIdealState` so every
   * table's empty state has the same icon/title/description proportions.
   *
   * One state covers both "nothing exists yet" and "nothing matches the
   * filters": the toolbar stays mounted either way, so the filters themselves
   * show which case it is, and a table that swapped in a different component
   * for the unfiltered case lost its column header along with it.
   */
  emptyState?: NonIdealStateProps;
  isLoadingMore?: boolean;
  /**
   * Render skeleton rows through the real header and cell markup, so the loading
   * state has identical column widths and row heights to the loaded table.
   */
  loading?: boolean | { rows?: number };
  /**
   * Existing rows are retained while replacement data loads. Unlike `loading`,
   * this does not replace them with skeletons; it only exposes the busy state
   * to assistive technology.
   */
  updating?: boolean;
  onScrollToEnd?: () => void;
  rowHeight?: BasicTableV3RowHeight;
  scrollToIndex?: number;
  /** Reveals a hidden/offscreen column and briefly highlights its header. A new requestId repeats the action. */
  columnFocus?: { columnId: string; requestId: number };
  /**
   * How `scrollToIndex` positions the row. "center" (the default) suits a one-off
   * jump to a deep-linked row; "auto" suits row-at-a-time keyboard navigation,
   * leaving an already-visible row put instead of recentring on every step.
   */
  scrollToIndexAlign?: "auto" | "center" | "start" | "end";
  /**
   * Blank space after the final row inside the table's scrollable content.
   * Use this when a floating element overlays the bottom of a clamped table,
   * so the final row can scroll above it without shrinking the scrollport.
   */
  scrollEndPaddingPx?: number;
  /** Show Back to top on upward scroll in full-page mode. Defaults to true; false opts out. */
  showBackToTopWhenSticky?: boolean;
  /**
   * Offset the sticky column header by this many pixels. Usually unset: a
   * sibling `BasicTableV3Toolbar` measures itself and supplies it through
   * context. Pass it only when the host pins its own chrome above the table.
   */
  stickyHeaderTop?: number;
  /**
   * Keep the standard horizontal separator below the final row. Frameless tables can use this
   * when their host supplies the outer frame but the rows still need a clear terminal edge.
   * Defaults to false so framed tables do not double their bottom border.
   */
  showLastRowSeparator?: boolean;
  superHeader?: ReactNode;
  /**
   * Content for an integrated super-header bar (entry count, download action)
   * rendered inside the table's rounded shell, above the column header. The
   * table owns the bar chrome — height, background, and the divider against
   * the column header — so pass only the row content. Under full-page
   * virtualization the bar lives inside the sticky header block and pins with
   * it. Prefer this over `superHeader`, which is a raw slot whose caller-styled
   * shell sits outside the table's rounded border.
   */
  superHeaderBar?: ReactNode;
  /** Row-axis virtualization. Independent of `virtualizeColumns` (the column axis). */
  rowVirtualization?: BasicTableV3Virtualization;
  /**
   * Horizontal column virtualization: only the columns within the horizontal
   * viewport (plus overscan) are rendered, with left/right spacer columns
   * standing in for the rest. Independent of `rowVirtualization` (the row axis) —
   * works in every row mode, including `"none"`. Intended for very wide tables.
   * Trade-offs while enabled: columns scrolled off-screen are not resize or
   * drag-drop targets (menu reorder still reaches them), and `fillAvailableWidth`
   * plus the last-column trailing-width stretch are disabled (both require every
   * column to be present/stretched, which conflicts with a windowed column set).
   * Defaults to true, except when `fillAvailableWidth` is set (then it defaults
   * to false so fill-width works); pass an explicit value to override either way.
   */
  virtualizeColumns?: boolean;
}
